import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaSearch, FaEye, FaCheckCircle, FaTimesCircle, FaClipboardList } from 'react-icons/fa';
import Card from '../components/common/Card';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import Spinner from '../components/common/Spinner';
import { SubmissionStatus } from '../types';
import {
  useSubmissions,
  useApproveSubmission,
  useRejectSubmission,
} from '../dry/hooks';
import { useLabel } from '../i18n/useLabel';

const Submissions: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const labelSubsPlural = useLabel('submission', { form: 'plural' });
  const labelFormsLower = useLabel('form', { form: 'plural', case: 'lower' });

  // Local UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<SubmissionStatus | 'all'>(
    (searchParams.get('status') as SubmissionStatus) || 'all'
  );

  // React Query hooks for data fetching
  const { data: submissionsData, isLoading } = useSubmissions({
    status: filterStatus !== 'all' ? filterStatus : undefined,
    limit: 100,
  });

  // Mutation hooks
  const approveSubmissionMutation = useApproveSubmission();
  const rejectSubmissionMutation = useRejectSubmission();

  // Extract data with defaults (useSubmissions already returns a typed array)
  const submissions = Array.isArray(submissionsData) ? submissionsData : [];

  const handleApprove = (id: string) => {
    approveSubmissionMutation.mutate({ id });
  };

  const handleReject = (id: string) => {
    const reason = window.prompt('Please provide a reason for rejection:');
    if (!reason) return;
    rejectSubmissionMutation.mutate({ id, notes: reason });
  };

  const filteredSubmissions = (submissions || []).filter(
    (submission) =>
      !searchQuery ||
      submission.form_template?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      submission.user?.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusColor = (
    status: SubmissionStatus
  ): 'success' | 'warning' | 'danger' | 'default' => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'submitted':
      case 'requires_action':
        return 'warning';
      case 'rejected':
        return 'danger';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{labelSubsPlural}</h1>
        <p className="text-gray-600 mt-1">Review and manage submitted {labelFormsLower}</p>
      </div>

      {/* Search and Filters */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search submissions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as SubmissionStatus | 'all')}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="draft">Draft</option>
            <option value="requires_action">Requires Action</option>
          </select>
        </div>
      </Card>

      {/* Results Count */}
      <p className="text-sm text-gray-600">
        Showing {filteredSubmissions?.length || 0} of {submissions?.length || 0} submissions
      </p>

      {/* Submissions List */}
      {filteredSubmissions.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <FaClipboardList className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No submissions found</h3>
            <p className="mt-2 text-sm text-gray-600">
              {searchQuery || filterStatus !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Submissions will appear here once forms are submitted'}
            </p>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Form</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    Submitted By
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Asset</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Completion</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Date</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredSubmissions.map((submission) => (
                  <tr
                    key={submission.id}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/submissions/${submission.id}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {submission.form_template?.name || 'Unknown Form'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900">
                        {submission.user?.first_name} {submission.user?.last_name}
                      </div>
                      <div className="text-xs text-gray-500">{submission.user?.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900">
                        {submission.asset?.name || '-'}
                      </div>
                      {submission.asset?.site && (
                        <div className="flex items-center gap-1 mt-0.5 text-xs text-indigo-600">
                          <svg className="w-3 h-3 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"/></svg>
                          <span>{submission.asset.site.name}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={getStatusColor(submission.status)}>
                        {submission.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      {submission.completion_rate !== undefined ? (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[100px]">
                            <div
                              className={`h-2 rounded-full ${
                                submission.completion_rate === 100
                                  ? 'bg-green-500'
                                  : submission.completion_rate >= 50
                                  ? 'bg-yellow-500'
                                  : 'bg-red-500'
                              }`}
                              style={{ width: `${submission.completion_rate}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600 whitespace-nowrap">
                            {submission.completed_fields}/{submission.total_fields} ({Math.round(submission.completion_rate)}%)
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(submission.submitted_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/submissions/${submission.id}`);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View"
                        >
                          <FaEye />
                        </button>
                        {submission.status === 'submitted' && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApprove(submission.id);
                              }}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Approve"
                            >
                              <FaCheckCircle />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReject(submission.id);
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Reject"
                            >
                              <FaTimesCircle />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default Submissions;
